import type { OAuthProvider } from '@doss/common-all'
import { User as FirebaseUser } from 'firebase/auth'
import { useCallback } from 'react'
import { useAuthState, useSignInWithEmailAndPassword, useSignOut } from 'react-firebase-hooks/auth'
import { auth } from '~/common/lib/firebase'
import { useAuthProviderType } from '~/common/utils/auth-provider'
import { useWorkOSAuthContext, type WorkOSUserInfo } from '../contexts/WorkOSAuthContext'

/**
 * Unified auth state and methods.
 * Abstracts away the dual Firebase/WorkOS handling into a single interface.
 */
type UseAuthReturn = {
  user: WorkOSUserInfo | FirebaseUser | null | undefined
  isLoading: boolean
  isSignedIn: boolean
  isSigningIn: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void> | void
  signInWithOAuth: (
    provider: OAuthProvider,
    options?: { devEmail?: string; state?: string },
  ) => Promise<void>
  error: Error | null
}

/**
 * Unified authentication hook that abstracts away Firebase vs WorkOS dual handling.
 *
 * This hook provides a consistent interface regardless of which auth provider is configured.
 * Components should use this instead of directly importing useWorkOSAuthContext or Firebase hooks.
 *
 * Features:
 * - Single import for all auth operations
 * - Automatic provider selection based on config.authProvider
 * - Consistent API across Firebase and WorkOS
 * - Type-safe with discriminated unions
 *
 * @returns Unified auth state and methods
 *
 * @example
 * const { user, isLoading, isSignedIn, signIn, signOut, error } = useAuth()
 *
 * const handleLogin = async () => {
 *   await signIn(email, password)
 * }
 */
export const useAuth = (): UseAuthReturn => {
  const { isWorkOS } = useAuthProviderType()

  // WorkOS authentication (when authProvider is 'workos')
  const workos = useWorkOSAuthContext()
  const {
    signIn: workosSignIn,
    signOut: workosSignOut,
    signInWithOAuth: workosSignInWithOAuth,
    ...workosRest
  } = workos

  // Firebase authentication (legacy, when authProvider is 'firebase')
  const [firebaseUser, loadingFirebase] = useAuthState(auth)
  const [firebaseSignIn, , firebaseIsSigningIn, firebaseSignInError] =
    useSignInWithEmailAndPassword(auth)
  const [firebaseSignOut] = useSignOut(auth)

  // Unified signIn function
  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (isWorkOS) {
        await workosSignIn(email, password)
      } else {
        const result = await firebaseSignIn(email, password)
        if (!result) {
          throw firebaseSignInError || new Error('Sign in failed')
        }
      }
    },
    [isWorkOS, workosSignIn, firebaseSignIn, firebaseSignInError],
  )

  // Unified signOut function
  const signOut = useCallback(async (): Promise<void> => {
    if (isWorkOS) {
      // WorkOS signOut is synchronous (clears state + dispatches events)
      workosSignOut()
    } else {
      // Firebase signOut is async (returns Promise<boolean>, we discard the result)
      await firebaseSignOut()
    }
  }, [isWorkOS, workosSignOut, firebaseSignOut])

  // Unified signInWithOAuth function
  const signInWithOAuth = useCallback(
    async (
      provider: OAuthProvider,
      options?: { devEmail?: string; state?: string },
    ): Promise<void> => {
      if (isWorkOS) {
        await workosSignInWithOAuth(provider, options)
      }
    },
    [isWorkOS, workosSignInWithOAuth],
  )
  // Return unified interface
  if (isWorkOS) {
    return {
      user: workosRest.user,
      isLoading: workosRest.isLoading,
      isSignedIn: workosRest.isSignedIn,
      isSigningIn: workosRest.isLoading, // WorkOS uses isLoading for signing in state
      signIn,
      signOut,
      signInWithOAuth,
      error: workosRest.error,
    }
  }

  return {
    user: firebaseUser,
    isLoading: loadingFirebase,
    isSignedIn: !!firebaseUser,
    isSigningIn: firebaseIsSigningIn,
    signIn,
    signOut,
    signInWithOAuth,
    error: firebaseSignInError || null,
  }
}
